/**
 * PR-A -- agent-facing copy for the Production workspace frame regions.
 *
 * WHY A SEPARATE MODULE FROM `productStateCopy.js`
 * ------------------------------------------------
 * `productStateCopy` holds the product's own *state* vocabulary -- the
 * names of lifecycle states and what may be claimed about them. These
 * strings are something else: they are what a structural region says
 * when the capability it is reserved for does not exist yet. Mixing the
 * two would invite a future reader to treat "Lead details aren't
 * available yet" as a lifecycle state, which it is not.
 *
 * THE ONE RULE EVERY STRING HERE OBEYS
 * ------------------------------------
 * A region that cannot do its job must say so plainly and must never
 * imply the capability is present, coming imminently, or merely loading.
 * WORKSPACE-FEATURE-001 S16.5 forbids blank panels and indefinite
 * spinners as empty states; it equally forbids pretending. "Not
 * available yet" is honest; a greyed-out control the agent might try to
 * click is not, which is why these regions render text rather than
 * disabled affordances.
 *
 * ZERO IS NOT UNKNOWN
 * -------------------
 * WORKSPACE-FEATURE-001 S7.8 requires that an unavailable count never
 * render as `0`. Every KPI slot in this frame is unavailable, so every
 * one of them uses `KPI_UNAVAILABLE_VALUE` and none of them renders a
 * number. The distinction is load-bearing: a `0` here would tell an
 * agent that they have made no calls today, which MissionAssist does
 * not know.
 */

export const KPI_UNAVAILABLE_VALUE = "Unavailable";
/** The value shown for every KPI slot whose backend contract does not
 * exist. Deliberately a word, never `0` and never an em dash -- S7.8
 * requires unknown and zero to stay distinguishable, and a bare dash is
 * ambiguous between them. */

export const KPI_UNAVAILABLE_NOTE =
  "Performance figures aren't available in the workspace yet.";
/** Said once for the whole region rather than repeated per card, so the
 * region reads as one honest statement instead of four alarms. */

export const LEAD_DETAILS_DEFERRED =
  "Prospect details aren't available here yet. Nothing about this lead is " +
  "shown rather than showing something that may not belong to this call.";
/** AW-2C owns canonical Lead context. The second sentence is the point:
 * it tells the agent the blankness is a deliberate safety choice, not a
 * failure to load, so nobody waits for it to populate. */

export const QUOTING_DEFERRED =
  "Quoting isn't available in the workspace yet.";
/** Flat and short on purpose. There is no partial quoting capability to
 * describe and no workaround to offer. */

export const CONTEXT_TOOLS_DEFERRED =
  "Supporting tools aren't available in the workspace yet. Each tool below " +
  "is shown in its designed position and is unavailable until it has a " +
  "contract.";
/** AW-VIS-2. The right-hand region now carries the authoritative tool
 * rail rather than a bare placeholder, so this says what the agent is
 * actually looking at: real positions, no capability. The second
 * sentence is the point -- nothing here is loading, and nothing will
 * populate on its own. */

export const TOOL_DEFERRED_REASON = Object.freeze({
  lead:
    "Customer details appear here once a call connects. Nothing is " +
    "shown between calls rather than details that may not belong to " +
    "this one.",
  discovery:
    "Discovery answers appear here once a call connects. Answers are " +
    "entered and saved in the Script.",
  quote:
    "Quote values appear here once a call connects. Values are entered " +
    "and saved in the Script.",
  calendar:
    "Appointments aren't shown here yet. The workspace can't tell which " +
    "prospect an appointment would belong to, so no schedule is listed " +
    "rather than one that may not be this call's.",
  actions:
    "There are no call actions yet. This tool has never had any, and it's " +
    "here so its position stays stable when the first one lands.",
  notes:
    "Notes aren't available in the workspace yet. Notes are stored against " +
    "a prospect record the workspace doesn't receive for the current call.",
  history:
    "There's no contact history to show yet. Prior-contact records aren't " +
    "reachable from the workspace.",
});
/** AW-VIS-3. One honest reason per tool, naming that tool's own blocker.
 *
 * These lines are now reached only BETWEEN calls, for the three tools
 * that have data when one is active -- so they say when the tool fills
 * in, not that it never will.
 *
 * AW-2C-005 CORRECTED THEM, and the old wording is worth recording
 * because it was true when written and became false quietly. Discovery
 * and Quote said their "field definitions aren't guaranteed to match
 * the Script version locked to this call"; AW-LDQ-002 made that
 * guarantee and AW-LDQ-003 rendered both panels from it. Lead said the
 * workspace "doesn't receive the lead record"; AW-2C-004 gave it one.
 * All three sentences survived the change that falsified them, which is
 * how copy ends up lying about a system that improved.
 *
 * Notes, History, Actions and Calendar still name a real present
 * blocker, each in the terms of the tool the agent just opened --
 * "not available yet" repeated four times teaches nothing. Calculator
 * has no entry: it works.
 *
 * Deliberately no "coming soon", no spinner, no date. S16.5 forbids
 * blank panels and indefinite spinners as empty states, and equally
 * forbids pretending: a promise nobody has made is worse than a blank.
 * The wording states a present fact and a reason, and stops. */

export const TOOL_READ_ONLY_NOTE =
  "This is a read-only view of what the Script has recorded for this " +
  "call. Answers are entered and saved in the Script.";
/** AW-LDQ-003. Says both halves: what the agent is looking at, and
 * where to change it. The panel's controls are genuinely disabled -- a
 * note alone would be the #685 defect in reverse, telling an agent
 * something is read-only while leaving it editable. */

export const CALENDAR_APPOINTMENTS_UNAVAILABLE =
  "Appointments aren't shown here yet. The workspace can't tell which " +
  "prospect an appointment would belong to, so no schedule is listed " +
  "rather than one that may not be this call's.";
/** AW-TOOLS-003. Said instead of an appointment list, never beside an
 * empty one. "No appointments scheduled" would assert a zero this
 * surface has not measured, and S7.8 requires unknown and zero to stay
 * distinguishable. The grid above it is fully usable; only the schedule
 * is absent, and this says which. */

export const CALL_BAR_RECORDING_UNAVAILABLE = "Recording unavailable";
/** AW-VIS-2. The reference's right-hand status area, restored in place
 * and claiming nothing. No authoritative recording-state contract
 * exists, and S15.2 forbids stale or invented call context -- so the
 * region keeps its designed location and states the absence. */

export const CALL_BAR_CONTROLS_UNAVAILABLE =
  "Call controls shown without a contract are unavailable. Hold, Mute, " +
  "Keypad, Voicemail, Transfer, Conference and Add Call perform no action " +
  "and send no command.";
/** S15.4 requires a disabled control to carry a reason, and says a
 * tooltip alone is insufficient for keyboard and touch users -- so this
 * is a real `sr-only` description referenced by every unavailable
 * control's `aria-describedby`. It names the controls explicitly rather
 * than saying "some controls", because an agent needs to know which. */

export const CALL_BAR_NO_CALL = "No active call";
/** Matches the historical reference's own resting label. Honest in every
 * non-call state: the bar is present, the controls are not applicable. */

export const CALL_BAR_IDENTITY_DEFERRED = "Caller information unavailable";
/** WORKSPACE-FEATURE-001 S15.2 requires honest language for unknown
 * identity rather than stale lead information. MissionAssist has no
 * customer identity on this surface at all -- the Production shell
 * deliberately holds none, and AW-2C owns it -- so this states the
 * absence instead of leaving a gap the agent might read as a name that
 * failed to render. */
